import {
  BookOpen,
  Layers,
  MessageSquare,
  RefreshCw,
  ScrollText,
  Send,
  X,
} from "lucide-react";
import type {
  RuleResponse,
  KnowledgeItemResponse,
  BatchResponse,
} from "../../gen/models";

export interface InPlaceComposerState {
  x?: number;
  y?: number;
  isAbove?: boolean;
  threadId?: string;
  messageId?: string;
  selectedText?: string;
  start?: number;
  end?: number;
  evaluationId?: string;
}

interface InPlaceCommentComposerProps {
  composer: InPlaceComposerState;
  composerText: string;
  composerRuleIds: string[];
  composerKnowledgeIds: string[];
  rulesList: RuleResponse[];
  knowledgeList: KnowledgeItemResponse[];
  activeBatch?: BatchResponse;
  savingComposer: boolean;
  mentionType: "rule" | "knowledge" | null;
  mentionQuery: string;
  mentionTarget: "composer" | "editor" | null;
  onOpenBatchModal?: () => void;
  onClose: () => void;
  onTextChange: (text: string) => void;
  onSave: () => void;
  onSelectMentionRule: (rule: RuleResponse) => void;
  onSelectMentionKnowledge: (knowledge: KnowledgeItemResponse) => void;
  onRemoveRule: (ruleId: string) => void;
  onRemoveKnowledge: (knowledgeId: string) => void;
}

export function InPlaceCommentComposer({
  composer,
  composerText,
  composerRuleIds,
  composerKnowledgeIds,
  rulesList,
  knowledgeList,
  activeBatch,
  savingComposer,
  mentionType,
  mentionQuery,
  mentionTarget,
  onOpenBatchModal,
  onClose,
  onTextChange,
  onSave,
  onSelectMentionRule,
  onSelectMentionKnowledge,
  onRemoveRule,
  onRemoveKnowledge,
}: InPlaceCommentComposerProps) {
  return (
    <div
      id="in-place-comment-composer"
      className="w-full max-w-full rounded-xl bg-white dark:bg-stone-900 border border-stone-300 dark:border-stone-700/80 shadow-md p-3 space-y-2.5 animate-in fade-in duration-150 text-left box-border overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            <span>{composer.selectedText ? "Comment on selected text" : "Add note"}</span>
          </div>
          {composer.selectedText && (
            <p className="text-[11px] text-stone-600 dark:text-stone-300 italic line-clamp-2 mt-1 border-l-2 border-amber-500 pl-2">
              "{composer.selectedText}"
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 p-0.5 rounded transition cursor-pointer shrink-0"
          title="Cancel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Textarea */}
      <div className="relative">
        <textarea
          autoFocus
          rows={3}
          placeholder={
            composer.selectedText
              ? "Write a comment on selected text... (@rule: or @knowledge: to link)"
              : "Write a note... (@rule: or @knowledge: to link)"
          }
          value={composerText}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              onSave();
            }
          }}
          className="w-full rounded-xl bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 p-2.5 text-xs text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:outline-hidden focus:ring-1 focus:ring-amber-500/50 resize-none transition"
        />

        {/* Mention popup */}
        {mentionType && mentionTarget === "composer" && (
          <div className="absolute left-0 bottom-full mb-1 z-60 w-full bg-white dark:bg-stone-900 rounded-xl shadow-xl ring-1 ring-stone-200 dark:ring-stone-800 max-h-48 overflow-y-auto p-1 text-xs">
            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 border-b border-stone-200 dark:border-stone-800">
              Mention {mentionType === "rule" ? "Rule" : "Knowledge Item"}
            </div>
            {mentionType === "rule" ? (
              rulesList
                .filter(
                  (r) =>
                    r.title.toLowerCase().includes(mentionQuery) ||
                    r.category.toLowerCase().includes(mentionQuery)
                )
                .slice(0, 5)
                .map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onSelectMentionRule(r)}
                    className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition flex items-center justify-between gap-2"
                  >
                    <span className="font-medium text-stone-800 dark:text-stone-200 truncate">
                      {r.title}
                    </span>
                    <span className="text-[9px] uppercase font-mono px-1 rounded bg-stone-100 dark:bg-stone-800 text-stone-500">
                      {r.category}
                    </span>
                  </button>
                ))
            ) : (
              knowledgeList
                .filter(
                  (k) =>
                    k.title.toLowerCase().includes(mentionQuery) ||
                    k.category.toLowerCase().includes(mentionQuery)
                )
                .slice(0, 5)
                .map((k) => (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => onSelectMentionKnowledge(k)}
                    className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition flex items-center justify-between gap-2"
                  >
                    <span className="font-medium text-stone-800 dark:text-stone-200 truncate">
                      {k.title}
                    </span>
                    <span className="text-[9px] uppercase font-mono px-1 rounded bg-stone-100 dark:bg-stone-800 text-stone-500">
                      {k.category}
                    </span>
                  </button>
                ))
            )}
          </div>
        )}
      </div>

      {/* Mentions Chips */}
      {(composerRuleIds.length > 0 || composerKnowledgeIds.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {composerRuleIds.map((rid) => {
            const r = rulesList.find((x) => x.id === rid);
            return (
              <span
                key={rid}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-200"
              >
                <ScrollText className="w-2.5 h-2.5" />
                <span>{r?.title || rid.slice(0, 8)}</span>
                <button
                  type="button"
                  onClick={() => onRemoveRule(rid)}
                  className="hover:text-amber-900 dark:hover:text-amber-100"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
          {composerKnowledgeIds.map((kid) => {
            const k = knowledgeList.find((x) => x.id === kid);
            return (
              <span
                key={kid}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-200"
              >
                <BookOpen className="w-2.5 h-2.5" />
                <span>{k?.title || kid.slice(0, 8)}</span>
                <button
                  type="button"
                  onClick={() => onRemoveKnowledge(kid)}
                  className="hover:text-indigo-900 dark:hover:text-indigo-100"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Active Batch & Actions */}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-stone-100 dark:border-stone-800/60">
        <div className="min-w-0 flex-1">
          {activeBatch ? (
            <button
              type="button"
              onClick={onOpenBatchModal}
              title={`Batch: ${activeBatch.title} (click to change)`}
              className="inline-flex items-center gap-1 text-[10px] text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition truncate max-w-full cursor-pointer"
            >
              <Layers className="w-2.5 h-2.5 shrink-0 opacity-60" />
              <span className="truncate">{activeBatch.title}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={onOpenBatchModal}
              className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 hover:underline cursor-pointer truncate max-w-full"
            >
              <Layers className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">Select Batch *</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-1 text-xs text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 font-medium transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={savingComposer || !composerText.trim() || !activeBatch}
            title={!activeBatch ? "An active batch is required" : undefined}
            className="px-3 py-1 rounded-lg bg-stone-900 hover:bg-stone-800 text-white dark:bg-stone-100 dark:hover:bg-white dark:text-stone-900 font-medium text-xs transition flex items-center gap-1.5 disabled:opacity-40 shadow-xs cursor-pointer shrink-0"
          >
            {savingComposer ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            <span>{composer.selectedText ? "Comment" : "Save note"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
